import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  IconButton,
  Stack,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { axiosInstance } from "src/network/adapter";
import { ApiEndPoints } from "src/network/endpoints";
import { toastError } from "src/utils/utils";
import moment from "moment";

const CulturalModuleDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [culturalModule, setCulturalModule] = useState(null);
  const [error, setError] = useState(null);

  const fetchCulturalModuleDetail = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(ApiEndPoints.CULTURAL.listById(id));
      setCulturalModule(response.data?.data?.module);
    } catch (error) {
      console.error("Error fetching cultural module detail:", error);
      setError("Failed to load cultural module details");
      toastError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) fetchCulturalModuleDetail();
  }, [id]);

  // ---- Loading State ----
  if (loading)
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );

  // ---- Error State ----
  if (error)
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
        <Button
          variant="contained"
          onClick={() => navigate("/cultural-module")}
          sx={{ mt: 2 }}
        >
          Back to Cultural Modules
        </Button>
      </Box>
    );

  if (!culturalModule)
    return (
      <Box p={3}>
        <Alert severity="warning">Cultural module not found</Alert>
        <Button
          variant="contained"
          onClick={() => navigate("/cultural-module")}
          sx={{ mt: 2 }}
        >
          Back to Cultural Modules
        </Button>
      </Box>
    );

  // ---- Main UI ----
  return (
    <Box sx={{ p: { xs: 2, md: 4 }, backgroundColor: "#fafafa", minHeight: "100vh" }}>
      {/* Header Section */}
      <Stack direction="row" alignItems="center" spacing={2} mb={3}>
        <IconButton
          onClick={() => navigate("/cultural-module")}
          sx={{
            bgcolor: "#fff",
            boxShadow: 1,
            "&:hover": { bgcolor: "#f5f5f5" },
          }}
        >
          <ArrowBackIcon />
        </IconButton>

        <Typography variant="h5" fontWeight="600" sx={{ flexGrow: 1 }}>
          Cultural Module Details
        </Typography>

      </Stack>

      <Grid container spacing={3}>
        {/* Basic Info */}
        <Grid item xs={12} md={8}>
          <Card
            sx={{
              borderRadius: 3,
              boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
              "&:hover": { boxShadow: "0 6px 25px rgba(0,0,0,0.08)" },
            }}
          >
            <CardContent>
              <Typography variant="h6" gutterBottom fontWeight={600}>
                Basic Information
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={2}>
                {[
                  { label: "Title", value: culturalModule.title },
                  { label: "Short Description", value: culturalModule.short_description },
                ].map((item, idx) => (
                  <Grid item xs={12} key={idx}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {item.label}
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      {item.value || "N/A"}
                    </Typography>
                  </Grid>
                ))}

                {/* Description rendered as HTML */}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Description
                  </Typography>
                  {culturalModule.description ? (
                    <Typography
                      variant="body1"
                      sx={{ mb: 2 }}
                      component="div"
                      dangerouslySetInnerHTML={{ __html: culturalModule.description }}
                    />
                  ) : (
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      N/A
                    </Typography>
                  )}
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Region
                  </Typography>
                  <Typography variant="body1">
                    {culturalModule.regionData?.name || "N/A"}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Status
                  </Typography>
                  <Chip
                    label={culturalModule.is_active ? "Active" : "Inactive"}
                    color={culturalModule.is_active ? "success" : "default"}
                    sx={{ mt: 0.5 }}
                  />
                </Grid>
              </Grid>

            </CardContent>
          </Card>
        </Grid>

        {/* AI Settings */}
        <Grid item xs={12} md={4}>
          <Card
            sx={{
              borderRadius: 3,
              boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
              "&:hover": { boxShadow: "0 6px 25px rgba(0,0,0,0.08)" },
            }}
          >
            <CardContent>
              <Typography variant="h6" gutterBottom fontWeight={600}>
                AI Settings
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {["Genre", "Scale", "Tempo"].map((field, i) => (
                <Box key={i} sx={{ mb: 1.5 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {field}
                  </Typography>
                  <Typography variant="body1">
                    {culturalModule.ai_setting?.[field] || "N/A"}
                  </Typography>
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>

        {/* Sample Files */}
        {culturalModule.sample_files?.length > 0 && (
          <Grid item xs={12}>
            <Card
              sx={{
                borderRadius: 3,
                boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
                "&:hover": { boxShadow: "0 6px 25px rgba(0,0,0,0.08)" },
              }}
            >
              <CardContent>
                <Typography variant="h6" gutterBottom fontWeight={600}>
                  Sample Files ({culturalModule.sample_files.length})
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Grid container spacing={2}>
                  {culturalModule.sample_files.map((file, index) => (
                    <Grid item xs={12} sm={6} md={4} key={index}>
                      <Box
                        sx={{
                          border: "1px solid #e0e0e0",
                          borderRadius: 2,
                          overflow: "hidden",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                          transition: "0.3s",
                          "&:hover": {
                            transform: "translateY(-4px)",
                            boxShadow: "0 6px 15px rgba(0,0,0,0.08)",
                          },
                        }}
                      >
                        <img
                          src={`${ApiEndPoints.MEDIA_URL}${file}`}
                          alt={`Sample file ${index + 1}`}
                          style={{
                            width: "100%",
                            height: "180px",
                            objectFit: "cover",
                            display: "block",
                          }}
                          onError={(e) => (e.target.style.display = "none")}
                        />
                        <Box p={1.5}>
                          <Typography
                            variant="caption"
                            noWrap
                            title={file.split("/").pop()}
                          >
                            {file.split("/").pop()}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Timestamps */}
        <Grid item xs={12}>
          <Card
            sx={{
              borderRadius: 3,
              boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
              "&:hover": { boxShadow: "0 6px 25px rgba(0,0,0,0.08)" },
            }}
          >
            <CardContent>
              <Typography variant="h6" gutterBottom fontWeight={600}>
                Timestamps
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Created At
                  </Typography>
                  <Typography variant="body1">
                    {culturalModule.created_at
                      ? moment(culturalModule.created_at).format("MMM DD, YYYY hh:mm A")
                      : "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Updated At
                  </Typography>
                  <Typography variant="body1">
                    {culturalModule.updated_at
                      ? moment(culturalModule.updated_at).format("MMM DD, YYYY hh:mm A")
                      : "N/A"}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CulturalModuleDetail;
